import { clientConfig } from '@/config/clientConfig';
import { isAssetDtoId } from '@/entities/assets';
import { isPersonalIdentifierDocumentDtoId, isPrivateDocumentDtoId } from '@/entities/document';
import { ActivationStatus, Tags } from '@/entities/vfo/types';
import { Member, UserStatus } from '@/entities/vfo/types';
import { ApiDashboardResponseV2 } from '@/shared/api';
import { getPersonFullName } from '@/shared/helpers/names';
import { DtoId } from '@/shared/lib/sj-orm/models/base.dto';
import { IconName } from '@/shared/uikit/Icon/types';

type InvitationLinkParams = {
  token: string;
  email: string;
  tag: Tags;
  tenantProfileFirstName: string;
  tenantProfileLastName: string;
  firstName?: string;
  lastName?: string;
};

export const generateInvitationLink = ({
  token,
  email,
  tag,
  tenantProfileFirstName,
  tenantProfileLastName,
  firstName,
  lastName,
}: InvitationLinkParams) => {
  const params = new URLSearchParams({
    token,
    email,
    tag,
    tenantProfileFirstName,
    tenantProfileLastName,
  });

  if (firstName) {
    params.append('firstName', firstName);
  }

  if (lastName) {
    params.append('lastName', lastName);
  }

  const url = new URL('/vfo/join', clientConfig.selfUrl);

  params.forEach((value, key) => {
    url.searchParams.append(key, value);
  });

  return url.href;
};

export const getPriority = (analytics?: ApiDashboardResponseV2) => {
  const priority = analytics?.analyticsOverview.charts.priorityBreakdown;

  return {
    medium: priority?.medium ?? 0,
    high: priority?.high ?? 0,
    low: priority?.low ?? 0,
  };
};

export const getStatusDistribution = (analytics?: ApiDashboardResponseV2) => {
  const statusDistribution = analytics?.analyticsOverview.charts.taskStatusOverview;

  return {
    totalTasks: analytics?.analyticsOverview.kpi.totalTasks ?? 0,
    toDo: statusDistribution?.toDo ?? 0,
    inProgress: statusDistribution?.inProgress ?? 0,
    done: statusDistribution?.done ?? 0,
  };
};

export const getCasesKpi = (analytics?: ApiDashboardResponseV2) => {
  const casesAnalytics = analytics?.cases.analytics.kpi;

  return {
    total: casesAnalytics?.totalCases ?? 0,
    active: casesAnalytics?.activeCases ?? 0,
    empty: casesAnalytics?.emptyCases ?? 0,
  };
};

export const getTasksKpi = (analytics?: ApiDashboardResponseV2) => {
  const kpi = analytics?.analyticsOverview.kpi;
  const recentActivity = analytics?.analyticsOverview.charts.recentActivity;
  const taskActivity = analytics?.analyticsOverview.charts.taskActivity;
  return {
    totalTasks: kpi?.totalTasks ?? 0,
    overdueTasks: kpi?.overdueTasks ?? 0,
    tasksWithoutAssignee: kpi?.tasksWithoutAssignee ?? 0,
    newTasks: recentActivity?.newTasks ?? 0,
    dueSoon: taskActivity?.dueSoon ?? 0,
    updated: taskActivity?.updated ?? 0,
    completed: taskActivity?.completed ?? 0,
  };
};

export const getTaskHealth = (analytics?: ApiDashboardResponseV2) => {
  const analyticsOverview = analytics?.analyticsOverview;

  return {
    totalTasks: analytics?.analyticsOverview.kpi.totalTasks ?? 0,
    active: analyticsOverview?.charts.tasksHealth.activeTasks ?? 0,
    done: analyticsOverview?.charts.tasksHealth.doneTasks ?? 0,
  };
};

export const getRecentActivity = (analytics?: ApiDashboardResponseV2) => {
  const activity = analytics?.analyticsOverview.charts.recentActivity;

  return {
    newTasks: activity?.newTasks ?? 0,
    newDocuments: activity?.newDocuments ?? 0,
  };
};

export const getInvitationBreakdown = (analytics?: ApiDashboardResponseV2) => {
  const invitationBreakdown = analytics?.allMembers.analytics.charts.invitationBreakdown;
  return {
    invited: invitationBreakdown?.invited ?? 0,
    registered: invitationBreakdown?.registered ?? 0,
  };
};

export const getExternalIconByDtoId = (dtoId: DtoId): IconName | null => {
  if (isAssetDtoId(dtoId)) {
    return 'briefcase';
  }

  if (isPersonalIdentifierDocumentDtoId(dtoId)) {
    return 'identification-card';
  }

  if (isPrivateDocumentDtoId(dtoId)) {
    return 'files';
  }

  return null;
};

export const sortMembers = (members: Member[]) => {
  return [...members].sort((a, b) => {
    const getGroupPriority = (member: Member) => {
      if (member.status === UserStatus.ONLINE) return 0;
      if (
        member.status === UserStatus.OFFLINE &&
        member.activationStatus === ActivationStatus.ACTIVE
      )
        return 1;
      return 2; // Invited
    };

    const getDisplayName = (member: Member) => {
      const fullname = getPersonFullName(member, 'display');
      return fullname || member.email;
    };

    const priorityA = getGroupPriority(a);
    const priorityB = getGroupPriority(b);

    if (priorityA !== priorityB) {
      return priorityA - priorityB;
    }

    const nameA = getDisplayName(a);
    const nameB = getDisplayName(b);

    return nameA.localeCompare(nameB, 'en', { sensitivity: 'base' });
  });
};
